import React, { useState } from "react";
import api, { formatError } from "@/lib/api";
import { Trash2 } from "lucide-react";
import ConfirmDeleteRoom from "@/components/ConfirmDeleteRoom";

export default function SettingsTab({ room, onSaved, navigate }) {
  const [form, setForm] = useState({
    name: room.name,
    description: room.description,
    brand_color: room.brand_color,
    watermark_text: room.watermark_text,
    archived: room.archived,
    deal_type: room.deal_type || "Other",
    default_role: room.default_role || "user",
    allow_downloads: room.allow_downloads !== false,
    require_mfa: !!room.require_mfa,
    expires_at: room.expires_at ? room.expires_at.slice(0, 10) : "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ kind: "", text: "" });
  const [showDelete, setShowDelete] = useState(false);

  const save = async () => {
    setSaving(true); setMsg({ kind: "", text: "" });
    try {
      const body = { ...form };
      // Send empty string as null
      if (!body.expires_at) body.expires_at = null;
      else body.expires_at = new Date(body.expires_at + "T00:00:00Z").toISOString();
      const { data } = await api.patch(`/rooms/${room.id}`, body);
      onSaved(data);
      setMsg({ kind: "success", text: "Settings saved." });
    } catch (e) { setMsg({ kind: "error", text: formatError(e) }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* General */}
      <div className="border border-border bg-card p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl tracking-tight">General</h3>
          {room.archived && (
            <span className="px-2.5 py-1 rounded-full text-xs bg-muted text-muted-foreground">Archived</span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-mono">Room name</label>
            <input
              data-testid="settings-room-name"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="label-mono">Deal type</label>
            <select
              data-testid="settings-deal-type"
              value={form.deal_type} onChange={(e) => setForm({ ...form, deal_type: e.target.value })}
              className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {["M&A","Fundraising","Audit","IPO","Diligence","Partnership","Other"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="label-mono">Description</label>
          <textarea
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {/* Branding */}
      <div className="border border-border bg-card p-6 space-y-5">
        <h3 className="font-display text-xl tracking-tight">Branding</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-mono">Brand color</label>
            <div className="mt-1 flex gap-2 items-center">
              <input type="color" value={form.brand_color}
                onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                className="h-10 w-16 bg-background border border-border rounded-md" />
              <input value={form.brand_color}
                onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                className="flex-1 px-3 py-2 mono text-xs bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
          </div>
          <div>
            <label className="label-mono">Watermark text</label>
            <input value={form.watermark_text}
              onChange={(e) => setForm({ ...form, watermark_text: e.target.value })}
              placeholder="CONFIDENTIAL"
              className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </div>
      </div>

      {/* Access & Security */}
      <div className="border border-border bg-card p-6 space-y-5">
        <h3 className="font-display text-xl tracking-tight">Access &amp; Security</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-mono">Default role for invitees</label>
            <select value={form.default_role}
              onChange={(e) => setForm({ ...form, default_role: e.target.value })}
              className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="user">User (limited)</option>
              <option value="custom">Custom (limited mgmt)</option>
              <option value="admin">Administrator</option>
            </select>
          </div>
          <div>
            <label className="label-mono">Expires on (optional)</label>
            <input type="date" value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </div>
        <Toggle
          label="Require MFA"
          help="Members without MFA enabled cannot enter this room."
          checked={form.require_mfa}
          onChange={(v) => setForm({ ...form, require_mfa: v })}
        />
        <Toggle
          label="Allow downloads"
          help="Master switch — when off, no member can download any file regardless of permissions."
          checked={form.allow_downloads}
          onChange={(v) => setForm({ ...form, allow_downloads: v })}
        />
        <Toggle
          label="Archive this room"
          help="Archived rooms are read-only for admins and hidden from non-admin members."
          checked={form.archived}
          onChange={(v) => setForm({ ...form, archived: v })}
        />
      </div>

      <FlashMsg msg={msg} />
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50">
          {saving ? "Saving…" : "Save changes"}
        </button>
        <span className="mono text-[10px] text-muted-foreground">
          Created {room.created_at ? new Date(room.created_at).toLocaleDateString() : "—"}
        </span>
      </div>

      {/* Danger zone */}
      <div className="border-2 border-destructive/30 bg-destructive/5 p-6 rounded-md">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl tracking-tight text-destructive">Danger zone</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-lg">
              Deleting a data room is permanent and cascades through documents, members,
              permissions, Q&amp;A, checklist items, activity logs, and notifications.
              Consider archiving instead if you might need this data later.
            </p>
          </div>
          <button
            data-testid="settings-room-delete"
            onClick={() => setShowDelete(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground transition shrink-0"
          >
            <Trash2 className="w-4 h-4" /> Delete data room…
          </button>
        </div>
      </div>

      {showDelete && (
        <ConfirmDeleteRoom
          room={room}
          onClose={() => setShowDelete(false)}
          onDeleted={() => navigate("/app/rooms")}
        />
      )}
    </div>
  );
}

function Toggle({ label, help, checked, onChange }) {
  return (
    <label className="flex items-start justify-between gap-4 cursor-pointer">
      <div>
        <div className="text-sm font-medium">{label}</div>
        {help && <div className="text-xs text-muted-foreground mt-0.5 max-w-md">{help}</div>}
      </div>
      <button
        type="button"
        role="switch" aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex w-10 h-6 rounded-full transition ${checked ? "bg-primary" : "bg-secondary"}`}
      >
        <span className={`absolute top-0.5 ${checked ? "left-[18px]" : "left-0.5"} w-5 h-5 rounded-full bg-background transition-all`} />
      </button>
    </label>
  );
}

function FlashMsg({ msg }) {
  if (!msg?.text) return null;
  const tone = {
    success: "bg-accent/10 border-accent/30 text-accent",
    error: "bg-destructive/10 border-destructive/30 text-destructive",
    info: "bg-secondary border-border text-muted-foreground",
  }[msg.kind] || "bg-secondary border-border text-muted-foreground";
  return (
    <div className={`text-xs px-3 py-2 rounded-md border ${tone}`}>{msg.text}</div>
  );
}
